import { RefObject, useLayoutEffect, useRef, useState } from 'react';

/**
 * Returns a ref and a boolean indicating whether the element's content
 * overflows its visible width (i.e. text is truncated with ellipsis).
 * Uses a ResizeObserver so the value stays correct when the container resizes.
 *
 * @param deps - Extra dependencies that should trigger an overflow recheck
 * (e.g. the text content), since content changes at a fixed width won't
 * fire the ResizeObserver.
 */
export function useIsOverflowing<E extends HTMLElement>(
  deps: React.DependencyList = []
): [RefObject<E | null>, boolean] {
  const ref = useRef<E>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setIsOverflowing(el.scrollWidth > el.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- consumer passes deps that should trigger a recheck
  }, deps);

  return [ref, isOverflowing];
}
